<template>
  <div style="height:100%; width: 100%; display: flex; flex-direction: column; justify-content: flex-start;">
    <camera-cloning-panel
      :showModal="showModal"
      @close="showModal = false"
      :availableCameras="camerasForCloning"
      @clone="cloneSettings"
      />
    <div v-if="currentCamera" style="top:12rem;">
      <div v-if="currentCamera.logging" class="button-container" style="top:12rem;">
        <owal-button
          class="add-detectable-button"
          style="margin-right: .5rem;"
          @click="addNew()"
          tooltip="add detectable"
          >
          <font-awesome-layers slot="accessory" class="fa-lg">
            <font-awesome-icon
              icon="dna"
              class="secondary-icon"
              style="color: rgba(168, 168, 168, 1)"
              />
            <font-awesome-icon
              icon="plus"
              transform="shrink-6 left-6 down-4"
              />
          </font-awesome-layers>
        </owal-button>
      </div>
      <div v-if="currentCamera.logging" class="button-container" style="top:15rem;">
        <owal-button
          class="add-detectable-button"
          style="margin-right: .5rem;"
          @click="showModal = !showModal"
          tooltip="copy to"
          >
          <font-awesome-layers slot="accessory" class="fa-lg">
            <font-awesome-icon
              icon="video"
              class="secondary-icon"
              style="color: rgba(168, 168, 168, 1)"
              />
            <font-awesome-icon
              icon="arrow-right"
              transform="shrink-6 left-6 down-4"
              />
          </font-awesome-layers>
        </owal-button>
      </div>
    </div>
    <!-- Note about keys and reactivity: https://hackernoon.com/the-correct-way-to-force-vue-to-re-render-a-component-bde2caae34ad -->
    <img
      v-if="currentCameraUUID"
      v-lazy="url"
      class="snapshot-image"
      :style="`${imageSize}`"
      :key="currentCameraUUID"
      title="click to expand"
      @click="expandSnapshot = !expandSnapshot"
      >
    <config-form
      :formGroups="formGroups"
      @change="handleChange"
      @deactivate="handleDeactivate"
      />
  </div>
</template>

<script>
import { mapState, mapMutations, mapGetters } from 'vuex'
import { cameraFieldsFromConfig, detectionFieldsFromConfig } from './dnaFormHelper'
import ConfigForm from './ConfigForm'
import OwalButton from '@/libraries/owal-ui/buttons/OwalButton'
import CameraCloningPanel from './CameraCloningPanel'

export default {
  name: 'DNACameraSettingsView',
  components: {
    ConfigForm,
    OwalButton,
    CameraCloningPanel,
  },
  data () {
    return {
      expandSnapshot: false,
      showModal: false,
    }
  },
  methods: {
    ...mapMutations({
      setCameraDetectables: 'dnaConfiguration/setCameraDetectables',
      setCameras: 'dnaConfiguration/setCameras',
    }),
    handleChange (change) {
      const current = { ...this.camerasById[this.currentCameraUUID] }
      if (change.group !== 'cameras') {
        const camera = {
          group: 'cameras',
          parentId: ['camera', this.currentCameraUUID],
          values: { ...current },
        }
        this.$emit('change', camera)
        this.$emit('change', change)
      } else {
        const camera = {
          group: 'cameras',
          parentId: ['camera', this.currentCameraUUID],
          values: { ...current, ...change.values },
        }
        this.$emit('change', camera)
      }
    },
    addNew (cameraUUID, values) {
      const changeParentId = cameraUUID || this.currentCameraUUID
      const changeValues = values || {
        confidence: 0.8,
        detectable: this.detectables[0].class_name,
        id: this.detectables[0].id,
        enabled: true,
        retention: 7862400, // 3 months
      }
      const newId = `NEW-${Object.keys(this.cameraDetectablesById).length + 1}`
      const change = {
        group: 'detectables',
        parentId: [this.currentCameraUUID, newId],
        values: changeValues,
      }
      this.$emit('change', change)
      const current = { ...this.camerasById[changeParentId] }
      const cameraChange = {
        group: 'cameras',
        parentId: ['camera', changeParentId],
        values: { ...current, detectables: [ newId, ...current.detectables ] },
      }
      this.$emit('change', cameraChange)
    },
    handleDeactivate (change) {
      const camera = Object.assign(this.camerasById[this.currentCameraUUID])
      const cameraChange = {
        group: 'cameras',
        parentId: ['camera', this.currentCameraUUID],
        values: { ...camera, detectables: camera.detectables.filter(id => id !== change.parentId[1]) },
      }
      this.$emit('change', cameraChange)
      this.$emit('change', change)
    },
    cloneSettings (cloneTargets) {
      const mapped = cloneTargets.reduce((prev, cameraDescription) => {
        const cameraIndex = this.camerasForCloning.findIndex(camera => {
          return camera[1].description === cameraDescription
        })
        const cameraUUID = this.camerasForCloning[cameraIndex][0]
        Object.values(this.currentCameraDetectables).map(values => {
          prev.push({
            cameraUUID,
            values,
          })
        })
        return prev
      }, [])
      mapped.forEach(change => this.addNew(change.cameraUUID, change.values))
    },
  },
  computed: {
    ...mapGetters({
      authToken: 'authToken',
    }),
    ...mapState('dnaConfiguration', {
      camerasById: state => state.camerasById,
      cameraDetectablesById: state => state.cameraDetectablesById,
      currentCameraUUID: state => state.currentCameraUUID,
      detectables: state => state.detectables,
    }),
    currentCamera () {
      return this.camerasById[this.currentCameraUUID]
    },
    imageSize () {
      if (this.expandSnapshot) {
        return 'width: 70%;'
      } else return 'width: 25%;'
    },
    url () {
      return `https://svc-a.api.owal.io/api/snapshot/v1/snapshot/${this.currentCameraUUID}?token=${this.authToken}`
    },
    formGroups () {
      if (this.currentCamera) {
        let conditions = []
        if (this.currentCamera.detectables) {
          conditions = detectionFieldsFromConfig(this.currentCameraDetectables, this.detectables)
        }
        const info = cameraFieldsFromConfig(this.currentCamera)
        return [ info, ...conditions ]
      } else return []
    },
    currentCameraDetectables () {
      return Object.entries(this.cameraDetectablesById).reduce(function (prev, curr) {
        if (this.currentCamera.detectables.includes(curr[0])) {
          prev[curr[0]] = curr[1]
        }
        return prev
      }.bind(this), {})
    },
    camerasForCloning () {
      return Object.entries(this.camerasById).filter(camera => {
        return camera[1].uuid !== this.currentCameraUUID
      })
    },
  },
  mounted () {
  },
}
</script>

<style scoped>
.snapshot-image {
  margin: 2rem;
  cursor: pointer;
  border-radius: 1rem;
}
.button-container {
  position:absolute;
  z-index:100;
  right:10%;
  display: flex;
  flex-direction: column;
  padding-bottom: 1rem;
}
</style>
